/**
 * TRUS-722: whether a claim may be submitted, and why it is held.
 *
 * Reads GET /api/claims/{id}/ffs-readiness: payer enrollment, the CHW
 * G0019/G0022 rules (initiating visit, monthly minutes, unit limits) and how the
 * 837P carries the rendering provider. The same check holds Submit on the server.
 */
import React, { useEffect, useState } from 'react';
import { CheckCircle2, PauseCircle, XCircle } from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { claimsApi, type ClaimReadiness } from '@/services/api/claims';

const RULE_LABELS: Record<string, [string, string]> = {
  payer_enrollment: ['claims.readiness.rule_enrollment', 'Payer enrollment'],
  chi_initiating_visit: ['claims.readiness.rule_initiating_visit', 'Initiating visit (G0019/G0022)'],
  chi_g0019_minutes: ['claims.readiness.rule_g0019', 'G0019 minutes this month'],
  chi_g0022_minutes: ['claims.readiness.rule_g0022', 'G0022 units this month'],
};

export const ClaimReadinessPanel: React.FC<{ claimId: number }> = ({ claimId }) => {
  const { t } = useAppTranslation();
  const [readiness, setReadiness] = useState<ClaimReadiness | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setReadiness(null);
    setFailed(false);
    claimsApi
      .getClaimReadiness(claimId)
      .then((r) => {
        if (!cancelled) setReadiness(r);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [claimId]);

  if (failed) {
    return (
      <p className="text-sm text-ink-500" data-testid="claim-readiness-error">
        {t('claims.readiness.load_failed', 'Could not check whether this claim can be submitted.')}
      </p>
    );
  }
  if (!readiness) {
    return <p className="text-sm text-ink-500">{t('claims.readiness.loading', 'Checking submission readiness…')}</p>;
  }

  return (
    <div className="rounded-md border border-v2border p-3 space-y-2" data-testid="claim-readiness">
      <div className="flex items-center gap-2 text-sm font-semibold">
        {readiness.ready ? (
          <CheckCircle2 className="h-4 w-4 text-green-600" />
        ) : (
          <PauseCircle className="h-4 w-4 text-amber-600" />
        )}
        <span data-testid="claim-readiness-status">
          {readiness.ready
            ? t('claims.readiness.ready', 'Ready to submit')
            : t('claims.readiness.held', 'Held — not ready to submit')}
        </span>
      </div>
      <ul className="space-y-1 text-sm">
        {readiness.findings.map((f, i) => {
          const [key, fallback] = RULE_LABELS[f.rule] ?? ['', f.rule];
          return (
            <li key={`${f.rule}-${i}`} className="flex items-start gap-2">
              {f.passed ? (
                <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-green-600" />
              ) : (
                <XCircle className="h-4 w-4 mt-0.5 shrink-0 text-red-600" />
              )}
              <span>
                <span className="font-medium">{key ? t(key, fallback) : fallback}:</span> {f.detail}
              </span>
            </li>
          );
        })}
      </ul>
      <p className="text-xs text-ink-500">
        {t('claims.readiness.rendering', '837 rendering provider: {{value}}', {
          value: readiness.rendering_provider_837.entity_type
            ? `${readiness.rendering_provider_837.loop_2310B} (entity type ${readiness.rendering_provider_837.entity_type})`
            : readiness.rendering_provider_837.loop_2310B,
        })}
      </p>
    </div>
  );
};

export default ClaimReadinessPanel;
